import Color from "color"
import { PipetteIcon } from "lucide-react"
import * as SliderPrimitive from "@radix-ui/react-slider"
import {
  createContext,
  type HTMLAttributes,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

/* ── Context ────────────────────────────────────────────────────────── */

interface ColorPickerContextValue {
  hue: number
  saturation: number
  lightness: number
  setHue: (hue: number) => void
  setSaturation: (saturation: number) => void
  setLightness: (lightness: number) => void
}

const ColorPickerContext = createContext<ColorPickerContextValue | undefined>(
  undefined
)

const useColorPickerCtx = () => {
  const ctx = useContext(ColorPickerContext)
  if (!ctx) throw new Error("ColorPicker compound components must be used within <ColorPicker>")
  return ctx
}

/* ── Root ────────────────────────────────────────────────────────────── */

export interface ColorPickerProps extends HTMLAttributes<HTMLDivElement> {
  /** Current color value (any CSS-parseable string, e.g. "#FF0000") */
  value?: string
  /** Fallback when value is empty */
  defaultValue?: string
  /** Called with a hex string whenever the color changes */
  onChange?: (hex: string) => void
}

export function ColorPicker({
  value,
  defaultValue = "#000000",
  onChange,
  className,
  children,
  ...props
}: ColorPickerProps) {
  const parsed = useMemo(() => {
    try {
      return Color(value || defaultValue)
    } catch {
      return Color(defaultValue)
    }
  }, [value, defaultValue])

  const [hue, setHue] = useState(parsed.hue())
  const [saturation, setSaturation] = useState(parsed.saturationl())
  const [lightness, setLightness] = useState(parsed.lightness())

  // Sync internal state when the controlled value changes externally
  useEffect(() => {
    try {
      const c = Color(value || defaultValue)
      setHue(c.hue())
      setSaturation(c.saturationl())
      setLightness(c.lightness())
    } catch {
      // ignore unparseable values while the user types
    }
  }, [value, defaultValue])

  // Emit hex on internal changes (skip initial mount to avoid dirtying the form)
  const mountedRef = useRef(false)
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    if (!onChange) return
    const hex = Color.hsl(hue, saturation, lightness).hex()
    onChange(hex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hue, saturation, lightness])

  return (
    <ColorPickerContext.Provider
      value={{ hue, saturation, lightness, setHue, setSaturation, setLightness }}
    >
      <div
        className={cn("flex flex-col gap-3 rounded-xl bg-card p-3", className)}
        {...props}
      >
        {children}
      </div>
    </ColorPickerContext.Provider>
  )
}

/* ── 2-D saturation / lightness canvas ──────────────────────────────── */

export const ColorPickerSelection = memo(
  ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const { hue, saturation, lightness, setSaturation, setLightness } = useColorPickerCtx()

    // Derive thumb position from context so it stays in sync with hex input / eyedropper
    const posX = saturation / 100
    const topLForPos = posX < 0.01 ? 100 : 50 + 50 * (1 - posX)
    const posY = lightness > 0 ? 1 - lightness / topLForPos : 1

    const bg = useMemo(
      () =>
        `linear-gradient(0deg, rgba(0,0,0,1), rgba(0,0,0,0)),
         linear-gradient(90deg, rgba(255,255,255,1), rgba(255,255,255,0)),
         hsl(${hue}, 100%, 50%)`,
      [hue]
    )

    const updateFromPointer = useCallback(
      (e: { clientX: number; clientY: number }) => {
        if (!containerRef.current) return
        const rect = containerRef.current.getBoundingClientRect()
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
        const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height))
        setSaturation(x * 100)
        const topL = x < 0.01 ? 100 : 50 + 50 * (1 - x)
        setLightness(topL * (1 - y))
      },
      [setSaturation, setLightness]
    )

    useEffect(() => {
      if (!isDragging) return
      const move = (e: PointerEvent) => updateFromPointer(e)
      const up = () => setIsDragging(false)
      window.addEventListener("pointermove", move)
      window.addEventListener("pointerup", up)
      return () => {
        window.removeEventListener("pointermove", move)
        window.removeEventListener("pointerup", up)
      }
    }, [isDragging, updateFromPointer])

    return (
      <div
        ref={containerRef}
        className={cn(
          "relative cursor-crosshair rounded-lg border border-border",
          className
        )}
        style={{ background: bg }}
        onPointerDown={(e) => {
          e.preventDefault()
          setIsDragging(true)
          updateFromPointer(e.nativeEvent)
        }}
        {...props}
      >
        <div
          className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white"
          style={{
            left: `${posX * 100}%`,
            top: `${posY * 100}%`,
            boxShadow: "0 0 0 1px rgba(0,0,0,0.3), 0 1px 4px rgba(0,0,0,0.2)",
          }}
        />
      </div>
    )
  }
)
ColorPickerSelection.displayName = "ColorPickerSelection"

/* ── Hue slider ─────────────────────────────────────────────────────── */

export function ColorPickerHue({ className }: { className?: string }) {
  const { hue, setHue } = useColorPickerCtx()

  return (
    <SliderPrimitive.Root
      className={cn("relative flex h-5 w-full touch-none select-none items-center", className)}
      max={360}
      step={1}
      value={[hue]}
      onValueChange={([v]) => setHue(v)}
    >
      <SliderPrimitive.Track className="relative h-3 w-full grow rounded-full bg-[linear-gradient(90deg,#FF0000,#FFFF00,#00FF00,#00FFFF,#0000FF,#FF00FF,#FF0000)]">
        <SliderPrimitive.Range className="absolute h-full" />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-primary bg-background shadow ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" />
    </SliderPrimitive.Root>
  )
}

/* ── Eyedropper button ──────────────────────────────────────────────── */

export function ColorPickerEyeDropper({ className }: { className?: string }) {
  const { setHue, setSaturation, setLightness } = useColorPickerCtx()

  const pick = async () => {
    try {
      // @ts-expect-error — EyeDropper API is experimental
      const dropper = new EyeDropper()
      const result = await dropper.open()
      const [h, s, l] = Color(result.sRGBHex).hsl().array()
      setHue(h)
      setSaturation(s)
      setLightness(l)
    } catch {
      // user cancelled or unsupported
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className={cn("h-9 w-9 shrink-0 rounded-xl", className)}
      onClick={pick}
    >
      <PipetteIcon className="h-3.5 w-3.5 text-muted-foreground" />
    </Button>
  )
}

/* ── Hex input ──────────────────────────────────────────────────────── */

export function ColorPickerHexInput({ className }: { className?: string }) {
  const { hue, saturation, lightness, setHue, setSaturation, setLightness } =
    useColorPickerCtx()

  const hex = useMemo(
    () => Color.hsl(hue, saturation, lightness).hex(),
    [hue, saturation, lightness]
  )

  const [draft, setDraft] = useState(hex)
  const [isFocused, setIsFocused] = useState(false)

  // Keep draft in sync when not editing
  useEffect(() => {
    if (!isFocused) setDraft(hex)
  }, [hex, isFocused])

  const commit = (raw: string) => {
    try {
      const c = Color(raw.startsWith("#") ? raw : `#${raw}`)
      const [h, s, l] = c.hsl().array()
      setHue(h)
      setSaturation(s)
      setLightness(l)
    } catch {
      setDraft(hex) // revert on bad input
    }
  }

  return (
    <Input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false)
        commit(draft)
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault()
          commit(draft)
          ;(e.target as HTMLInputElement).blur()
        }
      }}
      className={cn("h-9 flex-1 rounded-xl font-mono text-xs", className)}
    />
  )
}
